import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FieldLabel } from 'src/presentations/components/molecules/fields/FieldLabel';
import { FormContainer } from 'src/presentations/components/organisms/forms/FormContainer';
import { RhfMuiRadioGroup } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiRadioGroup';
import { RhfMuiTextArea } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiSimple';
import FormButtons from 'src/presentations/components/molecules/rhf-button/FormButtons';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { useConfirmInvoice, useRejectInvoice } from 'src/use-cases/invoice/useInvoiceActions';
import { openToastError } from 'src/use-cases/toast/useToast';

interface OpsAdminQuestionnaireProps {
  isReadOnly: boolean;
  onEdit: () => void;
  defaultValues?: {
    acceptInvoice?: string;
    reason?: string;
  };
}

interface FormValues {
  acceptInvoice?: string;
  reason?: string;
}

const ACCEPT_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

const validationSchema = Yup.object({
  acceptInvoice: Yup.string().required('Please select Yes or No'),
  reason: Yup.string().required('Please provide a reason'),
});

export const OpsAdminQuestionnaire: FC<OpsAdminQuestionnaireProps> = ({ isReadOnly, onEdit, defaultValues: initialValues }) => {
  const { invoiceId, checkVersion } = useInvoiceContext();
  const confirmInvoice = useConfirmInvoice(invoiceId);
  const rejectInvoice = useRejectInvoice(invoiceId);
  const isLoading = confirmInvoice.isLoading || rejectInvoice.isLoading;

  const formContext = useForm<FormValues>({
    defaultValues: { acceptInvoice: initialValues?.acceptInvoice ?? '', reason: initialValues?.reason ?? '' },
    resolver: yupResolver(validationSchema),
  });

  const values = formContext.watch();

  const handleSave = async (data: FormValues) => {
    checkVersion(async () => {
      try {
        if (data.acceptInvoice === 'yes') {
          await confirmInvoice.mutateAsync({ reason: data.reason });
        } else {
          await rejectInvoice.mutateAsync({ reason: data.reason, rejectReason: 'OTHER' });
        }
        onEdit();
      } catch (error) {
        openToastError(error as Error);
      }
    });
  };

  const handleCancel = () => {
    onEdit();
  };

  const acceptLabel = ACCEPT_OPTIONS.find((o) => o.id === values.acceptInvoice)?.label;

  return (
    <div className='mt-12 border-t border-dashed border-neutral-20 pt-12'>
      <FormContainer formContext={formContext} isReadOnly={isReadOnly} onSuccess={handleSave}>
        <FieldLabel label='Do you accept this invoice?'>
          {isReadOnly ? (
            <span className='text-14 xl:text-base font-semibold'>{acceptLabel || '-'}</span>
          ) : (
            <RhfMuiRadioGroup name='acceptInvoice' label='' options={ACCEPT_OPTIONS} />
          )}
        </FieldLabel>

        <FieldLabel label='Reason'>
          {isReadOnly ? (
            <span className='text-14 xl:text-base font-semibold'>{values.reason || '-'}</span>
          ) : (
            <RhfMuiTextArea name='reason' label='' placeholder='Reason goes here...' rows={3} />
          )}
        </FieldLabel>

        {!isReadOnly && (
          <div className='mt-14 xl:mt-16 pt-14 xl:pt-16'>
            <FormButtons onCancel={handleCancel} allowSubmitPristine submitButtonText='Save' disabled={isLoading} />
          </div>
        )}
      </FormContainer>
    </div>
  );
};
